import { useCallback, useMemo } from 'react';
import { getText } from 'i18n/intl';
import { fieldMappingSelector } from 'slices/field.slice';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import { reservationTemplatesSelector } from 'slices/reservationTemplate.slice';
import { useAppSelector } from 'slices/store';
import { v4 as uuid } from 'uuid';
import { FieldSettingType } from '@timeedit/calendar-integration-api-contract';
import type { MenuProps } from '@timeedit/ui-components/antd';
import type { TokenOptionsHelpers } from './EditTemplateModal';

const intlPrefix = 'integration.calendar.sync_settings';

/**
 * Hook that provides functions to build template field options for subject and location templates.
 */
export const useTemplateFieldOptions = () => {
  const objectTypes = useAppSelector(selectObjectTypesList);
  const fieldsMap = useAppSelector(fieldMappingSelector);
  const reservationTemplates = useAppSelector(reservationTemplatesSelector);

  const buildObjectFieldOptions = useCallback(
    (addField: TokenOptionsHelpers['addField']) => {
      return objectTypes.map((type) => ({
        key: `obj-${type.id}`,
        label: type.name,
        children: [
          ...new Set([...(type.fields || []), ...(type.parentFields || [])]),
        ].map((fieldId) => {
          const field = fieldsMap[fieldId];
          return {
            key: `obj-${type.id}-${fieldId}`,
            label: field ? `${type.name}.${field.name}` : String(fieldId),
            onClick: () => {
              addField({
                id: uuid(),
                type: FieldSettingType.objectField,
                objectTypeId: type.id,
                fieldId: fieldId,
              });
            },
          };
        }),
      }));
    },
    [objectTypes, fieldsMap]
  );

  const buildReservationFieldOptions = useCallback(
    (addField: TokenOptionsHelpers['addField']) => {
      return reservationTemplates.map((template) => ({
        key: `res-${template.id}`,
        label: template.name,
        children: (template.fields || []).map((fieldId) => {
          const field = fieldsMap[fieldId];
          return {
            key: `res-${template.id}-${fieldId}`,
            label: field ? field.name : fieldId,
            onClick: () => {
              addField({
                id: uuid(),
                type: FieldSettingType.reservationField,
                fieldId: fieldId,
              });
            },
          };
        }),
      }));
    },
    [reservationTemplates, fieldsMap]
  );

  const buildReservationInfoOptions = useCallback(
    (addField: TokenOptionsHelpers['addField']) => {
      return [
        {
          key: 'res-id',
          label: getText(
            `${intlPrefix}.form.sync_mode.te_out.subject.field_type.reservation_id`
          ),
          onClick: () => {
            addField({
              id: uuid(),
              type: FieldSettingType.reservationInfo,
              propertyName: 'id',
            });
          },
        },
      ];
    },
    []
  );

  const getSubjectTokenOptions = useCallback(
    ({
      addField,
      openStaticTextModal,
    }: TokenOptionsHelpers): MenuProps['items'] => {
      return [
        {
          key: FieldSettingType.objectField,
          label: getText(
            `${intlPrefix}.form.sync_mode.te_out.subject.field_type.object_field`
          ),
          children: buildObjectFieldOptions(addField),
        },
        {
          key: FieldSettingType.reservationField,
          label: getText(
            `${intlPrefix}.form.sync_mode.te_out.subject.field_type.reservation_field`
          ),
          children: buildReservationFieldOptions(addField),
        },
        {
          key: FieldSettingType.reservationInfo,
          label: getText(
            `${intlPrefix}.form.sync_mode.te_out.subject.field_type.reservation_info`
          ),
          children: buildReservationInfoOptions(addField),
        },
        {
          key: FieldSettingType.freeText,
          label: getText(
            `${intlPrefix}.form.sync_mode.te_out.subject.field_type.free_text`
          ),
          onClick: openStaticTextModal,
        },
      ];
    },
    [
      buildObjectFieldOptions,
      buildReservationFieldOptions,
      buildReservationInfoOptions,
    ]
  );

  const getLocationTokenOptions = useCallback(
    ({
      addField,
      openStaticTextModal,
    }: TokenOptionsHelpers): MenuProps['items'] => {
      return [
        {
          key: FieldSettingType.objectField,
          label: getText(
            `${intlPrefix}.form.sync_mode.te_out.subject.field_type.object_field`
          ),
          children: buildObjectFieldOptions(addField),
        },
        {
          key: FieldSettingType.reservationField,
          label: getText(
            `${intlPrefix}.form.sync_mode.te_out.subject.field_type.reservation_field`
          ),
          children: buildReservationFieldOptions(addField),
        },
        {
          key: FieldSettingType.freeText,
          label: getText(
            `${intlPrefix}.form.sync_mode.te_out.subject.field_type.free_text`
          ),
          onClick: openStaticTextModal,
        },
      ];
    },
    [buildObjectFieldOptions]
  );

  return {
    getSubjectTokenOptions,
    getLocationTokenOptions,
  };
};
